import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react';
import { ReactComponent as RotateIcon } from '@assets/icons/rotate_icon.svg';
import Input from '@components/forms/Input';
import Select from '@components/forms/Select';
import { Option } from '@material-tailwind/react';

import Button from '@components/forms/Button';
import { TableHeader } from '@components/widgets/VirtualTable';
import _isNil from 'lodash/isNil';
import { EmailStatusEnum, LenderStatusEnum, UserStatusEnum } from '@types';
import {
  post,
  LIST_LENDER_BROKERS,
  UPDATE_LENDER_USER,
  UPDATE_LENDER_BROKER,
  ADD_LENDER_BROKER,
  REMOVE_LENDER_BROKER,
} from '@services';
import { useAuthStore, useBrokerStore } from '@stores';
import {
  INVITATION_BOUNCED_LABEL,
  LENDER_STATUS_VARIANT
} from '@constants';
import StatusChip from '@components/widgets/StatusChip';
import { format, parseISO } from 'date-fns';
import { isBouncedInvitation } from '@utils/userUtils';
import VirtualTable from '@components/widgets/VirtualTable';
import Modal from '@components/widgets/Modal';
import { ReactComponent as MoreVertIcon } from '@assets/icons/more-icon.svg';

import {
  IconButton,
  Menu,
  MenuHandler,
  MenuItem,
  MenuList
} from '@material-tailwind/react';
import _debounce from 'lodash/debounce';
import AcceptRequestModal from './AcceptRequestModal';
import DeclineRequestModal from './DeclineRequestModal';

// Define BrokerUser interface
interface BrokerUser {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  lenderReference?: string;
  lastLogin?: string | null;
  status: LenderStatusEnum;
  emailStatus?: EmailStatusEnum | null;
  lenderOrganisationId: string;
  isAdmin?: boolean;
  roles?: string[]; // Added roles to interface
  // Add other relevant fields if needed
}

type SortDirection = 'asc' | 'desc';

const LandingTable = () => {
  const { selectedOrganisationId } = useAuthStore();
  const acceptRequest = (row: any) => {
    setRow(row);
    setOpenAccept(true);
  };

  const onConfirmBrokerAccessRequest = async (row: any, status: LenderStatusEnum) => {
    if (!row) return; // Guard clause if row is null
    try {
      await post(UPDATE_LENDER_BROKER, {
        userId: row.userId,
        lenderOrganisationId: selectedOrganisationId,
        status,
      });
      if (status === LenderStatusEnum.Active) {
        handleApiSuccess(setShowAcceptMessage, onCloseAccept);
      } else {
        handleApiSuccess(setShowDeclineMessage, onCloseDecline);
      }
    } catch (error) {
      console.error('Error accepting broker access request:', error);
    }
  };

  const declineRequest = (row: any) => {
    setRow(row);
    setOpenDecline(true);
  };

  const resetMFA = (row: any) => {
    setRow(row);
    setOpenMFA(true);
  };
  // const changeRole = (row: any) => {
  //   setRow(row);
  //   setOpenRole(true);
  // };
  const removeAccess = (row: any) => {
    setRow(row);
    setOpen(true);
  };
  const resendInvitation = (row: any) => {
    setRow(row);
    setOpenResend(true);
  };

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = useCallback(
    (key: string) => {
      if (sortKey === key) {
        // Cycle through 'asc', 'desc', null (no sort)
        if (sortDirection === 'asc') {
          setSortDirection('desc');
        } else if (sortDirection === 'desc') {
          setSortKey(null); // Reset sort key to remove sorting
          setSortDirection('asc'); // Reset direction for next sort
        }
      } else {
        // New column clicked, start with ascending sort
        setSortKey(key);
        setSortDirection('asc');
      }
    },
    [sortKey, sortDirection]
  );

  const TABLE_HEAD = useMemo(
    (): TableHeader[] => [
      {
        label: 'Username',
        field: 'email',
        contentClassName: 'text-grey-900 font-medium',
        onSort: handleSort
      },
      {
        label: 'Name',
        field: 'firstName', // Use firstName for sorting logic, render combines both
        render: (row) => {
          if (!row.firstName && !row.lastName) return '';
          if (!row.lastName) return row.firstName || '';
          if (!row.firstName) return row.lastName;
          return `${row.firstName} ${row.lastName}`;
        },
        onSort: handleSort
      },
      {
        label: 'Reference Number',
        field: 'lenderReference',
        onSort: handleSort
      },
      {
        label: 'Last Login Date',
        field: 'lastLogin',
        render: (row) =>
          row.lastLogin
            ? format(parseISO(row.lastLogin), 'dd/MM/yyyy h:mma')
            : '',
        onSort: handleSort
      },
      {
        label: 'Status',
        field: 'status',
        render: (row) => {
          const isBounced = isBouncedInvitation(row.status, row.emailStatus);

          return (
            <StatusChip
              variant={
                isBounced
                  ? 'error'
                  : LENDER_STATUS_VARIANT[row.status as LenderStatusEnum]
              }
              label={
                isBounced
                  ? INVITATION_BOUNCED_LABEL
                  : UserStatusEnum[row.status as keyof typeof UserStatusEnum]
              }
            />
          );
        },
        onSort: handleSort
      }
    ],
    [handleSort]
  );
  const [tableHeaders, setTableHeaders] = useState<TableHeader[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (userId) {
      const manageRoles = localStorage.getItem(`lenders_${userId}` as any);
      if (manageRoles) {
        try {
          const parsedRoles = JSON.parse(manageRoles);
          if (
            parsedRoles &&
            Array.isArray(parsedRoles) &&
            parsedRoles.length > 0 &&
            parsedRoles[0].roles
          ) {
            setRoles(parsedRoles[0].roles);
          } else {
            setRoles([]);
          }
        } catch (e) {
          console.error('Failed to parse roles from localStorage', e);
          setRoles([]);
        }
      } else {
        setRoles([]);
      }
    }
  }, []);

  const { query, status, brokerListData, setBrokerList, clearBrokerList } =
    useBrokerStore();

  const [row, setRow] = useState<BrokerUser | null>(null); // Use BrokerUser type
  const [openResend, setOpenResend] = useState(false);
  const [open, setOpen] = useState(false);
  const [openMFA, setOpenMFA] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showSaveMessage, setShowSaveMessage] = useState(false);
  const [showRemoveMessage, setShowRemoveMessage] = useState(false);
  const [showResendMessage, setShowResendMessage] = useState(false);
  const [openAccept, setOpenAccept] = useState(false);
  const [openDecline, setOpenDecline] = useState(false);
  const [showAcceptMessage, setShowAcceptMessage] = useState(false);
  const [showDeclineMessage, setShowDeclineMessage] = useState(false);
  // Background batches resolve after the user may have changed the search/status
  const latestFiltersRef = useRef({ query, status });
  latestFiltersRef.current = { query, status };
  const [originalBrokerList, setOriginalBrokerList] = useState<BrokerUser[]>(
    []
  ); // Keep track of filtered data for display
  const allFetchedDataRef = useRef<BrokerUser[]>([]); // Store all data fetched from API without filters using ref to avoid dependency issues
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false); // Auto-loading remaining pages
  const [isInitialLoading, setIsInitialLoading] = useState(true); // Loading state for initial page load
  // Bump to cancel in-flight background pagination when a fresh fetch starts
  const fetchGenerationRef = useRef(0);

  const applyClientFilters = useCallback(
    (data: BrokerUser[], currentQuery: string, currentStatus: string) => {
      let filteredData = [...data];

      const keyword = currentQuery.trim();
      if (keyword) {
        const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(escapedKeyword, 'i');
        filteredData = filteredData.filter((item: BrokerUser) => {
          if (pattern.test(item.email)) return true;
          if (item.firstName && pattern.test(item.firstName)) return true;
          if (item.lastName && pattern.test(item.lastName)) return true;
          const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
          if (fullName && pattern.test(fullName)) return true;
          return false;
        });
      }

      if (currentStatus !== 'all') {
        filteredData = filteredData.filter(
          (item: BrokerUser) =>
            item.status.toLowerCase() === currentStatus.toLowerCase()
        );
      }

      return filteredData;
    },
    []
  );

  // Fetch one listLenderBrokers page and add it to the visible list
  const fetchBrokerPage = useCallback(
    async (
      organisationId: string,
      startToken: string | null,
      isInitialLoad: boolean,
      generation: number
    ) => {
      if (generation !== fetchGenerationRef.current) {
        return {
          fetchedCount: 0,
          nextToken: null,
          failed: false,
          cancelled: true as const
        };
      }

      const currentUserIsAdmin =
        JSON.parse(
          localStorage.getItem(`lenders_${localStorage.getItem('userId')}`) ||
            '[]'
        )?.[0]?.roles?.includes('ADMIN') ?? false;

      try {
        const response = await post(LIST_LENDER_BROKERS, {
          lenderOrganisationId: organisationId,
          nextToken: startToken
        });

        if (generation !== fetchGenerationRef.current) {
          return {
            fetchedCount: 0,
            nextToken: null,
            failed: false,
            cancelled: true as const
          };
        }

        if (!response || !Array.isArray(response.lenderBrokers)) {
          console.error(
            'Invalid response structure from LIST_LENDER_BROKERS',
            response
          );
          return {
            fetchedCount: 0,
            nextToken: null,
            failed: true,
            cancelled: false as const
          };
        }

        const newData: BrokerUser[] = response.lenderBrokers;
        newData.forEach((broker: BrokerUser) => {
          broker.isAdmin = currentUserIsAdmin;
        });

        const updatedAllData = isInitialLoad
          ? newData
          : [...allFetchedDataRef.current, ...newData];
        allFetchedDataRef.current = updatedAllData;

        const { query: latestQuery, status: latestStatus } =
          latestFiltersRef.current;
        setOriginalBrokerList(
          applyClientFilters(updatedAllData, latestQuery, latestStatus)
        );

        return {
          fetchedCount: newData.length,
          nextToken: response.nextToken ?? null,
          failed: false,
          cancelled: false as const
        };
      } catch (error) {
        console.error('Error fetching lender brokers:', error);
        return {
          fetchedCount: 0,
          nextToken: null,
          failed: true,
          cancelled: false as const
        };
      }
    },
    [applyClientFilters]
  );

  // Keep fetching remaining pages in the background so search/filters cover the full list
  const loadRemainingBrokers = useCallback(
    async (
      organisationId: string,
      startToken: string,
      generation: number
    ) => {
      setIsBackgroundLoading(true);
      let token: string | null = startToken;
      try {
        while (token && generation === fetchGenerationRef.current) {
          const result = await fetchBrokerPage(
            organisationId,
            token,
            false,
            generation
          );
          // Stop on failure so a bad page isn't retried forever
          if (result.cancelled || result.failed) {
            break;
          }
          token = result.nextToken;
        }
      } finally {
        if (generation === fetchGenerationRef.current) {
          setIsBackgroundLoading(false);
        }
      }
    },
    [fetchBrokerPage]
  );

  // Define debounced fetch function
  const debouncedGetLenderList = useCallback(
    _debounce(
      async () => {
        if (!selectedOrganisationId) {
          console.warn('Organisation ID not found in lender data');
          return;
        }

        const generation = ++fetchGenerationRef.current;
        setIsBackgroundLoading(false);

        try {
          const { nextToken, failed, cancelled } = await fetchBrokerPage(
            selectedOrganisationId,
            null,
            true,
            generation
          );

          if (cancelled || generation !== fetchGenerationRef.current) {
            return;
          }

          setIsInitialLoading(false);

          // Auto-load the rest so filters/search apply to the full broker set
          if (nextToken && !failed) {
            void loadRemainingBrokers(
              selectedOrganisationId,
              nextToken,
              generation
            );
          }
        } catch (error) {
          console.error('Error loading lender brokers:', error);
          if (generation === fetchGenerationRef.current) {
            setIsInitialLoading(false);
          }
        }
      },
      600
    ),
    [fetchBrokerPage, loadRemainingBrokers, selectedOrganisationId]
  );

  // Define trigger function for fetching (always starts from the first page)
  const getLenderList = useCallback(() => {
    fetchGenerationRef.current += 1; // cancel any in-flight background load
    setIsBackgroundLoading(false);
    debouncedGetLenderList();
  }, [debouncedGetLenderList]);

  const onClose = () => {
    setOpen(false);
  };
  const onCloseMFA = () => {
    setOpenMFA(false);
  };
  const onCloseAccept = () => {
    setOpenAccept(false);
  };
  const onCloseDecline = () => {
    setOpenDecline(false);
  };
  const onCloseResend = () => {
    setOpenResend(false);
  };

  const handleApiSuccess = (
    setter: React.Dispatch<React.SetStateAction<boolean>>,
    closeFn: () => void,
    delay = 4000
  ) => {
    setter(true);
    getLenderList(); // Refresh list after action
    setTimeout(() => {
      setter(false);
      closeFn();
    }, delay);
  };

  const onConfirm = async () => {
    if (!row) return; // Guard clause if row is null
    try {
      await post(REMOVE_LENDER_BROKER, {
        lenderOrganisationId: selectedOrganisationId,
        userId: row.userId
      });
      handleApiSuccess(setShowRemoveMessage, onClose);
    } catch (error) {
      console.error('Error removing access:', error);
      // Optionally show an error message to the user
    }
  };

  const onConfirmResend = async () => {
    if (!row) return; // Guard clause if row is null
    try {
      await post(ADD_LENDER_BROKER, {
        email: row.email,
        lenderOrganisationId: selectedOrganisationId,
        lenderReference: row.lenderReference
      });
      handleApiSuccess(setShowResendMessage, onCloseResend);
    } catch (error) {
      console.error('Error resending invitation:', error);
      // Optionally show an error message to the user
    }
  };

  const onConfirmMFA = async () => {
    if (!row) return; // Guard clause if row is null
    try {
      await post(UPDATE_LENDER_BROKER, {
        userId: row.userId,
        lenderOrganisationId: selectedOrganisationId,
        isMfaEnabled: false
      });
      handleApiSuccess(setShowSuccessMessage, onCloseMFA);
    } catch (error) {
      console.error('Error resetting MFA:', error);
      // Optionally show an error message to the user
    }
  };

  const onConfirmRole = useCallback(
    async (role: string) => {
      // Explicitly type role
      if (!row) return; // Guard clause if row is null
      try {
        // Assuming organisationId might be on the row object, adjust if needed
        // Using lenderOrganisationId which is definitely on BrokerUser type
        await post(UPDATE_LENDER_USER, {
          lenderOrganisationId: selectedOrganisationId,
          userId: row.userId,
          roles: [role]
        });
        handleApiSuccess(setShowSaveMessage, () => setOpenRole(false)); // Use inline close function
      } catch (error) {
        console.error('Error updating user role:', error);
        // Optionally show an error message to the user
      }
    },
    [row, getLenderList] // Add getLenderList dependency
  );

  const sortedData = useMemo(() => {
    // Use originalBrokerList as the source of truth before sorting
    const dataToSort: BrokerUser[] = originalBrokerList || [];

    if (!sortKey || dataToSort.length === 0) {
      // If no sort key, return the original fetched list directly
      return dataToSort;
    }

    const sorted = [...dataToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // --- Get values based on sortKey ---
      if (sortKey === 'firstName') {
        // Combine firstName and lastName for sorting by Name
        aValue = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
        bValue = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
      } else if (sortKey === 'lastLogin') {
        // Use timestamp for date sorting, handle nulls
        aValue = a.lastLogin ? parseISO(a.lastLogin).getTime() : null; // Use null for consistency
        bValue = b.lastLogin ? parseISO(b.lastLogin).getTime() : null;
      } else {
        // General case: get value and convert string to lowercase
        aValue = a[sortKey as keyof BrokerUser];
        bValue = b[sortKey as keyof BrokerUser];
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      }

      // --- Handle nil/empty values: Always place them last ---
      const isANil = _isNil(aValue) || aValue === '';
      const isBNil = _isNil(bValue) || bValue === '';

      if (isANil && isBNil) return 0; // Both nil/empty, treat as equal
      if (isANil) return 1; // a is nil/empty, b is not, so a comes after b
      if (isBNil) return -1; // b is nil/empty, a is not, so b comes after a

      // --- Neither is nil/empty, proceed with comparison (ascending order) ---
      let comparison = 0;
      if (sortKey === 'lastLogin') {
        // Handle date comparison (already numbers)
        comparison = (aValue as number) - (bValue as number);
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        // Fallback comparison for mixed types (convert to string)
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return comparison; // Return raw comparison result (for ascending sort)
    });

    // Apply direction *after* sorting logic based on ascending order
    return sortDirection === 'desc' ? sorted.reverse() : sorted;
  }, [originalBrokerList, sortKey, sortDirection]); // Depend on original list now

  // Update brokerList in the store whenever sortedData changes
  useEffect(() => {
    setBrokerList(sortedData);
  }, [sortedData, setBrokerList]);

  // Effect for setting headers and triggering data fetch
  const prevDepsRef = useRef<{
    query: string;
    status: string;
    roles: string[];
  }>();
  const isInitialMount = useRef(true);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const lenders = localStorage.getItem(`lenders_${userId}`);
    let isAdmin = false;
    if (lenders) {
      try {
        const lenderData = JSON.parse(lenders);
        isAdmin = lenderData?.[0]?.roles?.includes('ADMIN') ?? false;
      } catch (e) {
        console.error('Failed to parse lender data in roles effect', e);
      }
    }

    const shouldShowActionItems = (status: LenderStatusEnum, action: string): boolean => {
      switch (action) {
        case 'resendInvitation':
          return [
            LenderStatusEnum.Pending,
            LenderStatusEnum.InvitationExpired,
          ].includes(status);
        case 'resetMFA':
          return [
            LenderStatusEnum.Active,
          ].includes(status);
        case 'acceptRequest':
          return [
            LenderStatusEnum.AccessRequested,
          ].includes(status);
        case 'declineRequest':
          return [
            LenderStatusEnum.AccessRequested,
          ].includes(status);
        case 'removeAccess':
          return [
            LenderStatusEnum.Active,
            LenderStatusEnum.Declined,
          ].includes(status);
      }
      return false;
    };

    // Define action column config - depends on isAdmin
    const actionColumn: TableHeader = {
      label: '',
      field: 'action', // Ensure this field doesn't conflict with actual data fields
      render: (rowItem: BrokerUser) => (
        // Use BrokerUser type
        <>
          {/* Do not show menu for the logged-in user themselves */}
          {rowItem.userId !== userId && (
            <Menu>
              <MenuHandler>
                <IconButton variant="text" size="sm">
                  {/* Adjusted size */}
                  <MoreVertIcon />
                </IconButton>
              </MenuHandler>
              <MenuList>
                {shouldShowActionItems(rowItem.status, 'resendInvitation') && (
                  <MenuItem
                  onClick={() => resendInvitation(rowItem)}
                  className="font-medium text-grey-700 text-sm">
                    Resend Invitation
                  </MenuItem>
                )}
                {shouldShowActionItems(rowItem.status, 'resetMFA') && (
                  <MenuItem
                    onClick={() => resetMFA(rowItem)}
                    className="font-medium text-grey-700 text-sm">
                    Reset MFA
                  </MenuItem>
                )}
                {shouldShowActionItems(rowItem.status, 'acceptRequest') && (
                  <MenuItem
                    onClick={() => acceptRequest(rowItem)}
                    className="font-medium text-grey-700 text-sm">
                    Accept request
                  </MenuItem>
                )}
                {shouldShowActionItems(rowItem.status, 'declineRequest') && (
                  <MenuItem
                    onClick={() => declineRequest(rowItem)}
                    className="font-medium text-grey-700 text-sm">
                    Decline access
                  </MenuItem>
                )}
                {shouldShowActionItems(rowItem.status, 'removeAccess') && (
                  <MenuItem
                  onClick={() => removeAccess(rowItem)}
                    className="font-medium text-rose-700 text-sm">
                    Remove access
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          )}
        </>
      )
    };

    // Set headers based on admin status and the base TABLE_HEAD
    setTableHeaders(isAdmin ? [...TABLE_HEAD, actionColumn] : TABLE_HEAD);

    // Determine if filters or roles have changed
    const prevDeps = prevDepsRef.current;
    const filtersChanged =
      !prevDeps ||
      prevDeps.query !== query ||
      prevDeps.status !== status ||
      prevDeps.roles !== roles; // Compare roles array reference

    // Fetch data only on initial mount or when roles change
    if (isInitialMount.current) {
      // Initial mount - fetch data
      clearBrokerList();
      getLenderList(); // Fetch initial data
    } else if (filtersChanged && prevDeps && prevDeps.roles !== roles) {
      // Roles changed - need to refetch
      allFetchedDataRef.current = [];
      setOriginalBrokerList([]);
      clearBrokerList();
      getLenderList();
    }

    // Update refs for next render
    isInitialMount.current = false;
    prevDepsRef.current = { query, status, roles };

    // Cleanup function
    return () => {
      debouncedGetLenderList.cancel(); // Cancel any pending debounced calls
    };
    // Dependencies: Include everything that might trigger a header update or a data fetch.
  }, [
    roles,
    TABLE_HEAD,
    getLenderList,
    clearBrokerList,
    debouncedGetLenderList
  ]);

  // Cancel in-flight background pagination only on unmount (not on filter keystrokes)
  useEffect(() => {
    return () => {
      fetchGenerationRef.current += 1;
    };
  }, []);

  // Optimized filtering with memoization
  const filteredData = useMemo(() => {
    // Skip if no data
    if (allFetchedDataRef.current.length === 0) {
      return [];
    }

    let result = allFetchedDataRef.current;

    // Apply keyword filtering
    const keyword = query.trim().toLowerCase();
    if (keyword) {
      // Cache the searchable text for each item to avoid repeated string operations
      result = result.filter((item: BrokerUser) => {
        // Create searchable text once per item
        const searchText = [
          item.email?.toLowerCase() || '',
          item.firstName?.toLowerCase() || '',
          item.lastName?.toLowerCase() || '',
          `${item.firstName || ''} ${item.lastName || ''}`.trim().toLowerCase()
        ].join(' ');

        return searchText.includes(keyword);
      });
    }

    // Apply status filtering
    if (status !== 'all') {
      const statusLower = status.toLowerCase();
      result = result.filter(
        (item: BrokerUser) => item.status.toLowerCase() === statusLower
      );
    }

    return result;
  }, [query, status, allFetchedDataRef.current.length]); // Only re-run when these change

  // Update originalBrokerList when filtered data changes
  useEffect(() => {
    if (!isInitialMount.current) {
      setOriginalBrokerList(filteredData);
    }
  }, [filteredData]);

  const [openRole, setOpenRole] = useState(false);
  const onCloseRole = () => {
    setOpenRole(false);
  };

  return (
    <div className="pb-12">
      {/* Show loading animation during initial load */}
      {isInitialLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-grey-600 text-sm">Loading brokers...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Pass the sortedData to the VirtualTable component */}
          <VirtualTable
            headers={tableHeaders} // Use the state variable for headers
            data={brokerListData} // Table displays data from the store, which is updated by the useEffect above
            sortKey={sortKey}
            sortDirection={sortDirection}
            itemHeight={64}
          />
          {/* Background auto-load of remaining broker pages */}
          {isBackgroundLoading && brokerListData.length > 0 && (
            <div className="flex justify-center items-center gap-2 pt-6 text-grey-600 text-sm">
              <RotateIcon className="w-4 h-4 animate-spin" />
              Loading the rest of your brokers…
            </div>
          )}
        </>
      )}
      {/* Modals */}
      <ResetMFAModal
        open={openMFA}
        row={row}
        showSuccessMessage={showSuccessMessage}
        onClose={onCloseMFA}
        onConfirm={onConfirmMFA}
      />
      <AcceptRequestModal
        open={openAccept}
        row={row}
        showSuccessMessage={showAcceptMessage}
        onClose={onCloseAccept}
        onConfirm={(row: any) => onConfirmBrokerAccessRequest(row, LenderStatusEnum.Active)}
      />
      <DeclineRequestModal
        open={openDecline}
        row={row}
        showSuccessMessage={showDeclineMessage}
        onClose={onCloseDecline}
        onConfirm={(row: any) => onConfirmBrokerAccessRequest(row, LenderStatusEnum.Declined)}
      />
      <RemoveAccessModal
        open={open}
        row={row}
        onClose={onClose}
        showSuccessMessage={showRemoveMessage}
        onConfirm={onConfirm}
      />
      <ChangeRoleModal
        open={openRole}
        row={row}
        showSuccessMessage={showSaveMessage}
        onClose={onCloseRole}
        onConfirm={(selectedRole: string) => onConfirmRole(selectedRole)} // Pass selected role up
      />
      <ResendInvitationModal
        open={openResend}
        row={row}
        showSuccessMessage={showResendMessage}
        onClose={onCloseResend}
        onConfirm={onConfirmResend} // Corrected prop name
      />
    </div>
  );
};
// Define props interface for modal components
interface ModalProps {
  open: boolean;
  row: BrokerUser | null; // Use BrokerUser type, allow null if row might be initially unset
  onClose: () => void;
  showSuccessMessage: boolean;
  onConfirm: (args?: any) => void; // Make args optional or specific per modal
}

const RemoveAccessModal: React.FC<ModalProps> = ({
  open,
  row,
  onClose,
  showSuccessMessage,
  onConfirm
}) => {
  return (
    <Modal title="Remove Access" size="md" open={open} onClose={onClose}>
      <div className="flex flex-col gap-[10px] p-2">
        {' '}
        {/* Added padding */}
        <div className="text-grey-700">
          {' '}
          {/* Adjusted text color */}
          <p>
            Are you sure you want to remove access for{' '}
            <span className="font-semibold text-grey-900">
              {' '}
              {/* Adjusted font weight/color */}
              {row?.email ?? 'this user'} {/* Use nullish coalescing */}
            </span>{' '}
            from this broker organisation?
          </p>
        </div>
        <div className="mt-4">
          {' '}
          {/* Increased margin top */}
          <div className="h-6 w-full text-right mb-2">
            {' '}
            {/* Adjusted height/margin */}
            {showSuccessMessage && (
              <div className="text-green-600 text-sm font-medium">
                Access removed!
              </div> // Adjusted styling
            )}
          </div>
          <div className="grid grid-cols-2 w-full gap-3">
            {' '}
            {/* Adjusted gap */}
            <Button
              variant="outlined"
              onClick={onClose}
              className="border bg-white border-grey-300 text-grey-700 normal-case font-semibold rounded-lg text-sm py-2 px-4 w-full focus:ring-2 focus:ring-offset-1 focus:ring-primary-focus">
              {' '}
              {/* Adjusted styling */}
              Cancel
            </Button>
            <Button
              className="font-semibold bg-primary normal-case rounded-lg text-base text-white w-auto whitespace-nowrap"
              onClick={onConfirm}>
              Remove Access
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// Define props specifically for ChangeRoleModal if needed, extending ModalProps
interface ChangeRoleModalProps extends ModalProps {
  onConfirm: (role: string) => void; // Specific onConfirm signature
}

const ChangeRoleModal: React.FC<ChangeRoleModalProps> = ({
  open,
  row,
  onClose,
  showSuccessMessage,
  onConfirm
}) => {
  // Determine initial role safely, defaulting to STANDARD if roles array is missing or empty
  // Ensure 'roles' property exists on the row object or handle its absence
  const initialRole = row?.roles?.includes('ADMIN') ? 'ADMIN' : 'STANDARD';
  const email = row?.email ?? ''; // Use nullish coalescing
  const [selectedRole, setSelectedRole] = useState<string>(initialRole);

  // Update selectedRole if the row or initialRole changes (e.g., modal reused for different user)
  useEffect(() => {
    // Ensure 'roles' property exists
    const currentInitialRole = row?.roles?.includes('ADMIN')
      ? 'ADMIN'
      : 'STANDARD';
    setSelectedRole(currentInitialRole);
  }, [row]); // Rerun when row changes

  // Type the event parameter for clarity, though 'any' is often used with third-party Selects
  const handleRoleChange = (value: string | undefined) => {
    if (value) {
      // Ensure value is not undefined
      setSelectedRole(value);
    }
  };

  return (
    <Modal title="Change Broker's Role" size="md" open={open} onClose={onClose}>
      <div className="flex flex-col gap-4 p-2">
        {' '}
        {/* Adjusted gap and padding */}
        <div>
          <p className="text-sm font-medium text-grey-700 mb-1.5">Email</p>
          <Input readOnly placeholder="Email" type="email" value={email} />{' '}
          {/* Use readOnly */}
        </div>
        <div>
          <p className="text-sm font-medium text-grey-700 mb-1.5">Role</p>
          {/* Ensure Select component props are correct and type-safe */}
          <Select
            value={selectedRole}
            onChange={handleRoleChange} // Pass the handler
            // Apply necessary styling classes, ensure compatibility with Element Plus/Headless UI if used
            className="w-full" // Example class
            placeholder="Select role" // Add placeholder if needed
          >
            {/* Use Option component correctly */}
            <Option value={'ADMIN'}>Admin</Option>
            <Option value={'STANDARD'}>Standard</Option>
          </Select>
        </div>
        <div className="mt-2">
          <div className="h-6 w-full text-right mb-2">
            {' '}
            {/* Adjusted height/margin */}
            {showSuccessMessage && (
              <div className="text-green-600 text-sm font-medium">
                Changes saved!
              </div> // Adjusted styling
            )}
          </div>
          <div className="flex gap-3 justify-end">
            {' '}
            {/* Adjusted gap */}
            <Button
              variant="outlined"
              onClick={onClose}
              className="border bg-white border-grey-300 text-grey-700 normal-case font-semibold rounded-lg text-sm py-2 px-4 w-auto focus:ring-2 focus:ring-offset-1 focus:ring-primary-focus">
              {' '}
              {/* Adjusted styling */}
              Cancel
            </Button>
            <Button
              className="font-semibold bg-primary normal-case rounded-lg text-sm text-white w-auto py-2 px-4 whitespace-nowrap hover:bg-primary-dark focus:ring-2 focus:ring-offset-1 focus:ring-primary-focus" // Adjusted styling
              onClick={() => onConfirm(selectedRole)}>
              Save
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// Define props specifically for ResendInvitationModal
interface ResendInvitationModalProps extends ModalProps {
  // onConfirm doesn't need args here
  onConfirm: () => void;
}

const ResendInvitationModal: React.FC<ResendInvitationModalProps> = ({
  open,
  row,
  onClose,
  showSuccessMessage,
  onConfirm
}) => {
  const email = row?.email ?? '';

  return (
    <Modal title="Resend Invitation" size="md" open={open} onClose={onClose}>
      <div className="flex flex-col gap-4 p-2">
        {' '}
        {/* Adjusted gap/padding */}
        <div>
          <p className="text-sm font-medium text-grey-700 mb-1.5">Email</p>
          <Input readOnly placeholder="Email" type="email" value={email} />{' '}
          {/* Use readOnly */}
        </div>
        <div className="mt-2">
          <div className="h-6 w-full text-right mb-2">
            {' '}
            {/* Adjusted height/margin */}
            {showSuccessMessage && (
              <div className="text-green-600 text-sm font-medium">
                Invite sent!
              </div> // Adjusted styling
            )}
          </div>
          <div className="flex gap-3 justify-end">
            {' '}
            {/* Adjusted gap */}
            <Button
              variant="outlined"
              onClick={onClose}
              className="border bg-white border-grey-300 text-grey-700 normal-case font-semibold rounded-lg text-sm py-2 px-4 w-auto focus:ring-2 focus:ring-offset-1 focus:ring-primary-focus">
              {' '}
              {/* Adjusted styling */}
              Cancel
            </Button>
            <Button
              className="font-semibold bg-primary normal-case rounded-lg text-sm text-white w-auto py-2 px-4 whitespace-nowrap hover:bg-primary-dark focus:ring-2 focus:ring-offset-1 focus:ring-primary-focus" // Adjusted styling
              onClick={onConfirm}>
              Resend Invite
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// Define props specifically for ResetMFAModal
interface ResetMFAModalProps extends ModalProps {
  // onConfirm doesn't need args here
  onConfirm: () => void;
}

const ResetMFAModal: React.FC<ResetMFAModalProps> = ({
  open,
  row,
  onClose,
  showSuccessMessage,
  onConfirm
}) => {
  return (
    <Modal title="Reset MFA" size="md" open={open} onClose={onClose}>
      <div className="flex flex-col gap-4 p-2">
        {' '}
        {/* Adjusted gap/padding */}
        <div className="text-grey-700">
          {' '}
          {/* Adjusted text color */}
          <p>
            Resetting the Multi-Factor Authentication (MFA) for{' '}
            <span className="font-semibold text-grey-900">
              {' '}
              {/* Adjusted style */}
              {row?.email ?? 'this user'}
            </span>{' '}
            will require this user to set up their MFA method again upon next
            login.
          </p>
          <p className="mt-3">Are you sure you want to proceed?</p>{' '}
          {/* Adjusted margin */}
        </div>
        <div className="mt-2">
          <div className="h-6 w-full text-right mb-2">
            {' '}
            {/* Adjusted height/margin */}
            {showSuccessMessage && (
              <div className="text-green-600 text-sm font-medium">
                MFA Reset Successfully!
              </div> // Adjusted styling
            )}
          </div>
          <div className="grid grid-cols-2 w-full gap-3 ">
            {' '}
            {/* Adjusted gap */}
            <Button
              variant="outlined"
              onClick={onClose}
              className="border bg-white border-grey-300 text-grey-700 normal-case font-semibold rounded-lg text-sm py-2 px-4 w-full focus:ring-2 focus:ring-offset-1 focus:ring-primary-focus">
              {' '}
              {/* Adjusted styling */}
              Cancel
            </Button>
            <Button
              className="font-semibold bg-primary normal-case rounded-lg text-sm text-white w-full py-2 px-4 whitespace-nowrap hover:bg-primary-dark focus:ring-2 focus:ring-offset-1 focus:ring-primary-focus" // Adjusted styling
              onClick={onConfirm}>
              Reset MFA
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
export default LandingTable;
